import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, X, Send, Pause, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function VoiceRecorder({ onSend, onCancel }) {
  const [state, setState] = useState('recording'); // recording | paused | stopped
  const [duration, setDuration] = useState(0);
  const [waveData, setWaveData] = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const streamRef = useRef(null);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  // Start recording on mount
  useEffect(() => {
    let cancelled = false;

    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        // Set up analyser for waveform
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64;
        source.connect(analyser);
        analyserRef.current = analyser;

        // Visualize waveform
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const updateWave = () => {
          analyser.getByteFrequencyData(dataArray);
          // Take a subset of frequencies for visualization (16 bars)
          const bars = [];
          const step = Math.floor(bufferLength / 16);
          for (let i = 0; i < 16; i++) {
            bars.push(dataArray[i * step] / 255);
          }
          setWaveData(prev => [...prev.slice(-40), bars]);
          animFrameRef.current = requestAnimationFrame(updateWave);
        };
        updateWave();

        // MediaRecorder
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : 'audio/ogg';

        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start(100);
        startTimer();
      } catch (err) {
        console.error('Mic access denied:', err);
        toast.error('Microphone access denied', { description: 'Please allow microphone permissions in your browser settings' });
        onCancel();
      }
    };

    startRecording();

    return () => {
      cancelled = true;
      stopTimer();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const handlePause = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      stopTimer();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      setState('paused');
    }
  };

  const handleResume = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      startTimer();
      // Restart waveform animation
      const analyser = analyserRef.current;
      if (analyser) {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const updateWave = () => {
          analyser.getByteFrequencyData(dataArray);
          const bars = [];
          const step = Math.floor(bufferLength / 16);
          for (let i = 0; i < 16; i++) {
            bars.push(dataArray[i * step] / 255);
          }
          setWaveData(prev => [...prev.slice(-40), bars]);
          animFrameRef.current = requestAnimationFrame(updateWave);
        };
        updateWave();
      }
      setState('recording');
    }
  };

  const handleSend = () => {
    stopTimer();
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      // Already stopped, build blob from existing chunks
      const blob = new Blob(chunksRef.current, { type: recorder?.mimeType || 'audio/webm' });
      cleanup();
      onSend(blob, duration);
      return;
    }

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      cleanup();
      onSend(blob, duration);
    };
    recorder.stop();
  };

  const handleCancel = () => {
    stopTimer();
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    cleanup();
    onCancel();
  };

  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Render compact waveform (last 24 snapshots, just the average of each)
  const renderWaveform = () => {
    const recent = waveData.slice(-24);
    return (
      <div className="flex items-center gap-[2px] h-8 flex-1 min-w-0 mx-2">
        {recent.map((bars, i) => {
          const avg = bars.reduce((a, b) => a + b, 0) / bars.length;
          const h = Math.max(4, avg * 28);
          return (
            <div
              key={i}
              className="w-[3px] rounded-full bg-white/70 transition-all duration-75"
              style={{ height: `${h}px` }}
            />
          );
        })}
        {/* Fill remaining slots with static bars */}
        {Array.from({ length: Math.max(0, 24 - recent.length) }).map((_, i) => (
          <div key={`s-${i}`} className="w-[3px] h-1 rounded-full bg-white/30" />
        ))}
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#FF9933] to-[#E88A2D] flex-shrink-0" data-testid="voice-recorder">
      {/* Cancel button */}
      <button
        onClick={handleCancel}
        className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors flex-shrink-0"
        data-testid="voice-cancel-btn"
      >
        <Trash2 className="w-5 h-5 text-white" />
      </button>

      {/* Waveform + Timer */}
      <div className="flex-1 flex items-center min-w-0">
        {/* Recording indicator */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-2.5 h-2.5 rounded-full ${state === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-white/50'}`} />
          <span className="text-white font-mono text-sm font-semibold min-w-[40px]" data-testid="voice-duration">
            {formatDuration(duration)}
          </span>
        </div>
        {renderWaveform()}
      </div>

      {/* Pause/Resume */}
      <button
        onClick={state === 'recording' ? handlePause : handleResume}
        className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors flex-shrink-0"
        data-testid="voice-pause-btn"
      >
        {state === 'recording' ? (
          <Pause className="w-5 h-5 text-white" />
        ) : (
          <Play className="w-5 h-5 text-white ml-0.5" />
        )}
      </button>

      {/* Send */}
      <button
        onClick={handleSend}
        className="w-11 h-11 rounded-full bg-white text-[#FF9933] flex items-center justify-center shadow-lg hover:scale-105 transition-transform flex-shrink-0"
        data-testid="voice-send-btn"
      >
        <Send className="w-5 h-5 ml-0.5" />
      </button>
    </div>
  );
}
